import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  Input,
  Box,
  ModalFooter,
  Button,
  Grid,
  Spacer,
  TextArea,
  Switch,
  Text,
} from "@flexisaf/flexibull2";
import { createForm } from "rc-form";
import Theme from "../../../theme";
import {
  selectIsCreatingGroup,
  selectIsEditingGroup,
} from "../../../redux/exam-group/selectors";
import { useSelector } from "react-redux";
import useSingleExamGroupGet from "../../../hooks/exam-groups/useSingleExamGroupGet";
import SectionLoader from "../../../components/custom/sectionLoader";
import useExamGroupEditor from "../../../hooks/exam-groups/useExamGroupEditor";
import { MyCropper } from "../../../components/ImageUploader/cropper";

export const ExamGroupEditor = ({ form, open, data, closeModal, cbProps }) => {
  const [editData, setEditData] = useState(null);
  const [image, setImage] = useState("");
  const [hasImage, setHasImage] = useState(false);
  const [active, setActive] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const isEditing = useSelector(selectIsEditingGroup);
  const isCreating = useSelector(selectIsCreatingGroup);

  const { getFieldError, getFieldProps, validateFields, resetFields } = form;
  const {
    groupData,
    loadingGroup,
    refetch: refetchSingleGroup,
    refetchGroups,
    resetGroupLoading,
  } = useSingleExamGroupGet({
    id: data?.id,
  });
  const { createGroup, editGroup } = useExamGroupEditor();

  useEffect(() => {
    if (data?.id && groupData) {
      setEditData(groupData);
      setActive(groupData?.active);
      setImageSrc(groupData?.imageUrl);
    }
    return () => {
      setEditData(null);
      setImageSrc(null);
      setHasImage(false);
    };
  }, [data?.id, groupData]);

  const handleClose = () => {
    resetFields();
    setImage(null);
    setActive(false);
    resetGroupLoading();
    closeModal();
    document.body.style.overflow = "auto";
  };

  const onSubmit = () => {
    validateFields((err, values) => {
      if (!err) {
        const { examGroupName, description } = values;
        let payload = {
          data: {
            name: examGroupName,
            description,
            isActive: active,
            image,
          },
          callback: () => {
            handleClose();
            refetchGroups();
          },
        };

        createGroup(payload);
      }
    });
  };

  const onEdit = () => {
    validateFields((err, values) => {
      if (!err) {
        const { examGroupName, description } = values;
        let payload = {
          data: {
            name: examGroupName,
            description,
            isActive: active,
            image,
            id: editData.id,
          },
          callback: () => {
            if (cbProps?.refetchGroups) {
              refetchGroups();
            }
            if (cbProps?.refetchGroup) {
              refetchSingleGroup();
            }
            handleClose();
          },
        };
        editGroup(payload);
      }
    });
  };
  const updateImage = () => {
    setHasImage(true);
  };

  return (
    <Modal outerclick onClose={() => handleClose} open={open}>
      <ModalBody width="40vw">
        {/* <ModalClose onClick={() => handleClose}>&times;</ModalClose> */}
        {data && loadingGroup ? (
          <SectionLoader height="60vh" />
        ) : (
          <>
            <Box
              pad="20px"
              style={{ borderBottom: `1px solid ${Theme.PrimaryBorderColor}` }}
            >
              <h3>{`${editData ? "Edit" : "Create"} Exam Group`}</h3>
            </Box>
            <Spacer space={20} />
            <Box pad="2rem">
              <Grid default="1fr">
                <Input
                  name="examGroupName"
                  placeholder="Ex: West African Examination Council"
                  label="Exam Group Name"
                  required
                  maxLength={32}
                  // disabled={edit}
                  error={
                    getFieldError("examGroupName")
                      ? "Exam Group Name is required"
                      : null
                  }
                  {...getFieldProps("examGroupName", {
                    initialValue: editData ? editData?.name : "",
                    rules: [{ required: true }],
                  })}
                />
              </Grid>

              <Spacer space={60} />
              <Grid default="1fr">
                <TextArea
                  name="description"
                  height="100px"
                  block
                  placeholder="Short description of the exam group"
                  label="Description"
                  required
                  // disabled={edit}
                  error={
                    getFieldError("description")
                      ? "Exam description is required"
                      : null
                  }
                  {...getFieldProps("description", {
                    initialValue: editData ? editData?.description : "",
                    rules: [{ required: true }],
                  })}
                />
              </Grid>
              <Spacer space={30} />
              <Box
                pad="20px 0"
                style={{
                  borderTop: `1px solid ${Theme.PrimaryBorderColor}`,
                  borderBottom: `1px solid ${Theme.PrimaryBorderColor}`,
                }}
              >
                <Text bold>Exam Active</Text>
                <Box />
                <Switch
                  size={20}
                  checked={active}
                  onChange={(e) => setActive(e.target.checked ? true : false)}
                  block
                />
              </Box>
              <Box
                pad="20px 0"
                align="center"
                style={{
                  borderBottom: `1px solid ${Theme.PrimaryBorderColor}`,
                }}
              >
                {!hasImage && (imageSrc === "" || imageSrc === null) ? (
                  <>
                    <Button onClick={() => setHasImage(true)}>
                      Upload Logo
                    </Button>
                  </>
                ) : (
                  <>
                    {hasImage && (
                      <>
                        <Box align="center" pad="10px 0">
                          <MyCropper
                            imageName="group_image"
                            onUpload={(file) => setImage(file)}
                            style={{ margin: "auto" }}
                          />
                        </Box>
                        <Box
                          display="flex"
                          style={{
                            width: "100%",
                            justifyContent: "center",
                            marginTop: "5px",
                          }}
                        >
                          <Button
                            onClick={() => {
                              setHasImage(false);
                              setImage(null);
                            }}
                            color={Theme.PrimaryRed}
                            fontColor="#fff"
                          >
                            {image ? "Delete" : "Cancel"}
                          </Button>
                        </Box>
                      </>
                    )}
                  </>
                )}
                {editData &&
                  imageSrc !== "" &&
                  imageSrc !== null &&
                  !hasImage && (
                    <Box align="center" pad="10px 0">
                      <Box>
                        <img src={imageSrc} alt="question_image" />
                      </Box>
                      <Box>
                        <Button onClick={updateImage} color={Theme.PrimaryRed}>
                          <i
                            className="saf-close-circle"
                            style={{ fontSize: "1.2rem" }}
                          />
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>
            <ModalFooter>
              <Box pad="20px">
                <Grid default="1fr 1fr">
                  <Box>
                    <Button pale color="transparent" onClick={handleClose}>
                      Cancel
                    </Button>
                  </Box>
                  <Box align="right">
                    {editData ? (
                      <Button onClick={onEdit} progress={isEditing}>
                        Edit
                      </Button>
                    ) : (
                      <Button onClick={onSubmit} progress={isCreating}>
                        Create
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Box>
            </ModalFooter>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default createForm()(ExamGroupEditor);
